import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, Grid, InputAdornment, MenuItem, MenuList, TextField, Typography } from '@mui/material';
import { ArrowDropDown, ArrowDropUp, Business, Group, Groups, Mail, Person, Phone, Save, Work } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';

import { LocalizationContext } from '@cvt/contexts';
import { emailRegex, phoneRegex } from '@cvt/helpers/validation';
import { ButtonDropdown } from '@cvt/components/ButtonDropdown';

import { CompanyAutocomplete } from '@modules/Companies/components/CompanyAutocomplete';

import { UserType } from '../enums';
import { UserContext } from '../contexts';
import { getUserTypeLabel } from '../helpers/user';
import { PictureUploader } from '../components/PictureUploader';


type FormCrud = Users.Crud;

type Fields = keyof (FormCrud & {
  submit: boolean;
});


interface Props {
    defaultValues?: Partial<FormCrud>;
    onSubmitRequest: (values: Users.Crud) => void;
    onSubmitButtonText: string;
    disabledFields?: Array<Fields>;
    hiddenFields?: Array<Fields>;
}

const DEFAULT_VALUES: Partial<Users.Crud> = {
  type: UserType.Customer,
  firstName: '',
  lastName: '',
  company: null,
  jobTitle: '',
  email: '',
};

export const UserForm: React.FC<Props> = ({ defaultValues = {}, onSubmitRequest, onSubmitButtonText,  disabledFields, hiddenFields }) => {

  const { isAdmin } = React.useContext(UserContext);
  const { dictionary } = React.useContext(LocalizationContext);

  const { handleSubmit, control, formState: { isSubmitting, errors }, watch } = useForm<Users.Crud>({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const isFieldDisabled = React.useCallback((field: Fields) => {
    if (!disabledFields) {
      return false;
    }
    return disabledFields.indexOf(field) !== -1;
  }, [disabledFields]);

  const isFieldVisible = React.useCallback((field: Fields) => {
    if (!hiddenFields) {
      return true;
    }
    return hiddenFields.indexOf(field) === -1;
  }, [hiddenFields]);

  const type = watch('type');

  const onSubmit = React.useCallback((data: Users.Crud) => {
    return onSubmitRequest(data);
  }, [onSubmitRequest]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        {isFieldVisible('profilePicture') && (
          <Grid size={isFieldVisible('type') ? 8 : 12}>
            <Controller
              name="profilePicture"
              control={control}
              render={({ field }) => (
                <PictureUploader
                  {...field}
                  disabled={isFieldDisabled('profilePicture')}
                  label={dictionary.forms.user.fieldPicture}
                />
              )}
            />
          </Grid>
        )}
        {isAdmin && isFieldVisible('type') && (
          <Grid size={isFieldVisible('profilePicture') ? 4 : 12} display="flex" justifyContent="flex-end">
            <Box>
              <Controller
                name="type"
                control={control}
                rules={{ required: dictionary.forms.validations.required }}
                render={({ field }) => (
                  <ButtonDropdown
                    label={type ? getUserTypeLabel(dictionary, type) : dictionary.forms.user.fieldType}
                    placement="bottom-start"
                    buttonProps={{
                      variant: 'text',
                      color: 'primary',
                      startIcon: <Groups/>,
                      endIcon: open => open ? <ArrowDropUp/> : <ArrowDropDown/>,
                      disabled: isFieldDisabled('type'),
                    }}
                    content={({ closeDropdown }) => (
                      <MenuList>
                        {isAdmin && (
                          <MenuItem
                            value={UserType.Admin}
                            onClick={() => {
                              field.onChange(UserType.Admin);
                              closeDropdown();
                            }}
                          >
                            {dictionary.users.labelTypeAdmin}
                          </MenuItem>
                        )}
                        <MenuItem
                          value={UserType.Staff}
                          onClick={() => {
                            field.onChange(UserType.Staff);
                            closeDropdown();
                          }}
                        >
                          {dictionary.users.labelTypeStaff}
                        </MenuItem>
                        <MenuItem
                          value={UserType.Customer}
                          onClick={() => {
                            field.onChange(UserType.Customer);
                            closeDropdown();
                          }}
                        >
                          {dictionary.users.labelTypeCustomer}
                        </MenuItem>
                      </MenuList>
                    )}
                  />
                )}
              />
            </Box>
          </Grid>
        )}
        {isFieldVisible('firstName') && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: dictionary.forms.validations.required }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={dictionary.forms.user.fieldName}
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Box ml={1} component={Person} fontSize="medium"/></InputAdornment>,
                  }}
                  disabled={isFieldDisabled('firstName')}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('lastName') && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: dictionary.forms.validations.required }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={dictionary.forms.user.fieldLastName}
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Box ml={1} component={Group} fontSize="medium"/></InputAdornment>,
                  }}
                  disabled={isFieldDisabled('lastName')}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('company') && isAdmin && type === UserType.Customer && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="company"
              control={control}
              rules={{ required: dictionary.forms.validations.required }}
              render={({ field, fieldState }) => (
                <CompanyAutocomplete
                  {...field}
                  label={dictionary.forms.user.fieldCompany}
                  TextFieldProps={{
                    error: !!fieldState.error,
                    size: 'small',
                    helperText: fieldState.error?.message,
                    InputProps: {
                      startAdornment: <InputAdornment position="start"><Box ml={1} component={Business} fontSize="medium"/></InputAdornment>,
                    },
                  }}
                  onChange={(e, value) => field.onChange(value)}
                  disabled={isFieldDisabled('company')}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('jobTitle') && (
          <Grid size={{ xs: 12, sm: isAdmin && type === UserType.Customer ? 6 : 12 }}>
            <Controller
              name="jobTitle"
              control={control}
              rules={{ required: dictionary.forms.validations.required }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={dictionary.forms.user.fieldPosition}
                  size="small"
                  error={!!errors.jobTitle}
                  helperText={errors.jobTitle?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Box ml={1} component={Work} fontSize="medium"/></InputAdornment>,
                  }}
                  disabled={isFieldDisabled('jobTitle')}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('email') && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: dictionary.forms.validations.required,
                pattern: {
                  value: emailRegex,
                  message: dictionary.forms.validations.invalidEmail,
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={dictionary.forms.fieldEmail}
                  size="small"
                  error={!!errors.email}
                  helperText={!isFieldDisabled('email') && defaultValues.email ? dictionary.forms.user.fieldEmailDisabledHelper : errors.email?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Box ml={1} component={Mail} fontSize="medium"/></InputAdornment>,
                  }}
                  disabled={isFieldDisabled('email') || !!defaultValues.email}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('phone') && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="phone"
              control={control}
              rules={{
                pattern: {
                  value: phoneRegex,
                  message: dictionary.forms.validations.invalidPhone,
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={(
                    <React.Fragment>
                      <Typography component="span" variant="inherit">{dictionary.forms.fieldPhone}</Typography>
                      <Typography component="span" variant="body2" color="gray" ml={1}>{dictionary.forms.labelOptional}</Typography>
                    </React.Fragment>
                  )}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Box ml={1} component={Phone} fontSize="medium"/></InputAdornment>,
                  }}
                  disabled={isFieldDisabled('phone')}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('submit') && (
          <Grid size={12}>
            <Grid container justifyContent="space-between" spacing={2}>
              <Box ml="auto"/>
              <Grid>
                <LoadingButton
                  loading={isSubmitting}
                  endIcon={<Save/>}
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isFieldDisabled('submit')}
                >
                  {onSubmitButtonText}
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </form>
  );
};
